import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Navigation } from "../components/Navigation";
import { Footer } from "../components/Footer";
import { pickLegal } from "../data/legalContent";

export default function LegalPage({ kind = "privacy" }) {
  const { i18n } = useTranslation();
  const content = pickLegal(kind, i18n.language);
  const isZh = (i18n.language || "en").startsWith("zh");

  useEffect(() => {
    document.documentElement.setAttribute("lang", i18n.language);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [i18n.language, kind]);

  return (
    <div data-testid={`legal-page-${kind}`}>
      <Navigation onWrite={() => {}} />

      <main className="max-w-3xl mx-auto px-6 py-20 md:py-28">
        <div className="mb-12">
          <Link
            to="/"
            data-testid="legal-back-home"
            className="inline-flex items-center gap-2 text-[12px] uppercase tracking-[0.22em] text-[var(--ink-soft)] hover:text-[var(--wax)] transition-colors mb-10"
          >
            <ArrowLeft size={12} /> {isZh ? "回首頁" : "Back to the front desk"}
          </Link>

          <p className="text-[11px] uppercase tracking-[0.4em] text-[var(--wax)] mb-5">
            {isZh ? "— 文件 —" : "— A quiet document —"}
          </p>
          <h1
            className={`${isZh ? "font-cn" : "font-display"} text-4xl md:text-5xl leading-tight tracking-tight text-[var(--ink)]`}
            data-testid="legal-title"
          >
            {content.title}
          </h1>
          <p className="mt-4 text-[12px] uppercase tracking-[0.28em] text-[var(--ink-soft)]" data-testid="legal-last-updated">
            {content.last_updated}
          </p>
        </div>

        <article
          className={`${isZh ? "font-cn" : "font-en"} text-[18px] md:text-[19px] leading-[1.95] text-[var(--ink)]`}
          data-testid="legal-body"
        >
          {/* Intro */}
          <p className="text-[var(--ink)] mb-12 italic">{content.intro}</p>

          {/* Hairline rule */}
          <div className="border-t border-[var(--rule)] my-12" />

          {content.sections.map((sec, idx) => (
            <section key={idx} className="mb-14" data-testid={`legal-section-${idx}`}>
              <h2
                className={`${isZh ? "font-cn whitespace-nowrap md:whitespace-normal" : "font-display break-words"} text-[19px] sm:text-xl md:text-[26px] font-bold md:font-medium tracking-tight text-[var(--ink)] mb-6`}
                data-testid={`legal-section-heading-${idx}`}
              >
                {sec.heading}
              </h2>

              {sec.lede && (
                <p className="mb-5 text-[var(--ink)]">{sec.lede}</p>
              )}

              {sec.bullets && (
                <ul className="space-y-4 mb-3 pl-1">
                  {sec.bullets.map((b, i) => (
                    <li key={i} className="flex gap-3" data-testid={`legal-bullet-${idx}-${i}`}>
                      <span aria-hidden className="select-none text-[var(--wax)] mt-1.5">·</span>
                      <span className="flex-1">
                        <span className={`${isZh ? "font-cn" : "font-display"} text-[var(--ink)] font-semibold`}>
                          {b[0]}
                        </span>{" "}
                        <span className="text-[var(--ink)]">{b[1]}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              {sec.paragraphs && sec.paragraphs.map((p, i) => (
                <p key={i} className="text-[var(--ink)] mb-4" data-testid={`legal-para-${idx}-${i}`}>
                  {p}
                </p>
              ))}
            </section>
          ))}

          <div className="border-t border-[var(--rule)] my-10" />
          <p className="text-[13px] italic text-[var(--ink-mute)] text-center">
            {isZh ? "— 情抒 · 給未來的信 —" : "— MiProse · A letter to the future —"}
          </p>
        </article>
      </main>

      <Footer />
    </div>
  );
}
